type SortDirection = "Ascending" | "Descending";

type SortColumn = {
  field: string;
  direction: SortDirection;
};

type CompactStateInput = {
  filters: Record<string, string>;
  sortColumns: SortColumn[];
  visibleColumns: string[];
};

type CompactStateDecoded = {
  filters: Record<string, string>;
  sortColumns: SortColumn[];
  visibleColumns: string[];
};

const FILTER_KEY_TO_ALIAS: Record<string, string> = {
  fundTypes: "ft",
  volatilityMin: "v0",
  volatilityMax: "v1",
  currentPvpMin: "p0",
  currentPvpMax: "p1",
  dy12mMin: "d0",
  dy12mMax: "d1",
  inadimplenciaMin: "i0",
  inadimplenciaMax: "i1",
  vacanciaMin: "x0",
  vacanciaMax: "x1",
  cashPressureMonths12mMin: "cp0",
  cashPressureMonths12mMax: "cp1",
  concentracaoTijoloMin: "t0",
  concentracaoTijoloMax: "t1",
  concentracaoPapelMin: "pp0",
  concentracaoPapelMax: "pp1",
  liquidezDiaria30dMin: "l0",
  liquidezDiaria30dMax: "l1",
  selicSensitivityScoreMin: "s0",
  selicSensitivityScoreMax: "s1",
  leverageIndexScoreMin: "a0",
  leverageIndexScoreMax: "a1",
  distanceTo12mHighMin: "h0",
  distanceTo12mHighMax: "h1",
  vpPerShareGrowth12mMin: "g0",
  vpPerShareGrowth12mMax: "g1",
  patrimonioMin: "r0",
  patrimonioMax: "r1",
  cotistasMin: "n0",
  cotistasMax: "n1",
  classificacao: "cl",
  segmento: "sg",
  cashTrendStatus: "cs",
  strategy: "st",
};

const FILTER_ALIAS_TO_KEY = Object.fromEntries(
  Object.entries(FILTER_KEY_TO_ALIAS).map(([key, alias]) => [alias, key]),
) as Record<string, string>;

const UPPERCASE_FILTER_KEYS = new Set(["classificacao", "cashTrendStatus"]);

const normalizeFilterValue = (key: string, value: string) => {
  const trimmed = value.trim();
  return UPPERCASE_FILTER_KEYS.has(key) ? trimmed.toUpperCase() : trimmed;
};

const COLUMN_FIELD_TO_ID: Record<string, number> = {
  fundTypeSymbol: 1,
  idCem: 2,
  name: 3,
  dy12mPct: 4,
  currentPvp: 5,
  numeroCotistas: 6,
  vacanciaPct: 7,
  inadimplenciaPct: 8,
  volatilityAnnualized21dPct: 9,
  cashPressureMonths12m: 10,
  cashTrendStatus: 11,
  distanceTo12mHighPct: 12,
  concentracaoTijoloPct: 13,
  concentracaoPapelPct: 14,
  liquidezDiaria30d: 15,
  vpPerShareGrowth12mPct: 16,
  patrimonioLiquido: 17,
  classificacao: 18,
  segmento: 19,
  selicSensitivityScore: 20,
  leverageIndexScore: 21,
};

const COLUMN_ID_TO_FIELD = Object.fromEntries(
  Object.entries(COLUMN_FIELD_TO_ID).map(([field, id]) => [id, field]),
) as Record<number, string>;

const MAX_COLUMN_ID = Math.max(...Object.values(COLUMN_FIELD_TO_ID));

export const CATALOGO_ALL_COLUMN_FIELDS = Object.keys(COLUMN_FIELD_TO_ID);
export const CATALOGO_BEGINNER_VISIBLE_COLUMNS = [
  "idCem",
  "name",
  "dy12mPct",
  "currentPvp",
  "liquidezDiaria30d",
];

function parseBase36Int(raw: string): number {
  const normalized = raw.trim().toLowerCase();
  if (!normalized) return 0;
  const alphabet = "0123456789abcdefghijklmnopqrstuvwxyz";
  let out = 0;
  for (const ch of normalized) {
    const idx = alphabet.indexOf(ch);
    if (idx < 0) return 0;
    out = out * 36 + idx;
  }
  return out;
}

export function encodeCatalogoVisibleColumnsBitset(
  visibleColumns: string[],
): string {
  let mask = 0;
  for (const field of visibleColumns) {
    const id = COLUMN_FIELD_TO_ID[field];
    if (!id) continue;
    mask += 2 ** (id - 1);
  }
  return mask.toString(36);
}

function decodeVisibleColumnsBitset(raw: string | number): string[] {
  const mask = typeof raw === "number" ? raw : parseBase36Int(raw);
  if (!Number.isFinite(mask) || mask <= 0) return [];
  const visibleColumns: string[] = [];
  for (let id = 1; id <= MAX_COLUMN_ID; id += 1) {
    const bit = 2 ** (id - 1);
    if (Math.floor(mask / bit) % 2 !== 1) continue;
    const field = COLUMN_ID_TO_FIELD[id];
    if (!field) continue;
    visibleColumns.push(field);
  }
  return visibleColumns;
}

export function buildCatalogoCompactQueryParams(
  input: CompactStateInput,
): URLSearchParams {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(input.filters)) {
    const alias = FILTER_KEY_TO_ALIAS[key];
    const normalized = normalizeFilterValue(key, String(value ?? ""));
    if (!alias || !normalized) continue;
    params.set(alias, normalized);
  }

  if (input.sortColumns.length > 0) {
    const encodedSort = input.sortColumns
      .map((item) => {
        const id = COLUMN_FIELD_TO_ID[item.field];
        if (!id) return null;
        const dir = item.direction === "Descending" ? "d" : "a";
        return `${id}${dir}`;
      })
      .filter((item): item is string => item !== null)
      .join(".");
    if (encodedSort) {
      params.set("o", encodedSort);
    }
  }

  const visibleBitset = encodeCatalogoVisibleColumnsBitset(
    input.visibleColumns,
  );
  if (visibleBitset && visibleBitset !== "0") {
    params.set("c", visibleBitset);
  }

  return params;
}

export function decodeCatalogoCompactFromParams(
  params: URLSearchParams,
): CompactStateDecoded | null {
  const filters: Record<string, string> = {};
  let hasCompactKey = false;

  params.forEach((value, alias) => {
    const key =
      FILTER_ALIAS_TO_KEY[alias] ??
      (FILTER_KEY_TO_ALIAS[alias] ? alias : undefined);
    if (!key) return;
    const normalized = normalizeFilterValue(key, String(value ?? ""));
    if (!normalized) return;
    filters[key] = normalized;
    hasCompactKey = true;
  });

  const sortColumns: SortColumn[] = [];
  const sortRaw = params.get("o");
  if (sortRaw) {
    hasCompactKey = true;
    for (const token of sortRaw.split(".")) {
      const match = token.match(/^(\d+)([ad])$/i);
      if (!match) continue;
      const field = COLUMN_ID_TO_FIELD[Number(match[1])];
      if (!field) continue;
      const direction: SortDirection =
        match[2].toLowerCase() === "d" ? "Descending" : "Ascending";
      sortColumns.push({ field, direction });
    }
  }
  const sortLegacyRaw = params.get("sort");
  if (!sortRaw && sortLegacyRaw) {
    hasCompactKey = true;
    for (const token of sortLegacyRaw.split(",")) {
      const [fieldRaw, dirRaw] = token.split(":");
      const field = (fieldRaw ?? "").trim();
      if (!field || !COLUMN_FIELD_TO_ID[field]) continue;
      const direction: SortDirection =
        (dirRaw ?? "").trim().toLowerCase() === "desc"
          ? "Descending"
          : "Ascending";
      sortColumns.push({ field, direction });
    }
  }

  let visibleColumns = CATALOGO_ALL_COLUMN_FIELDS;
  const columnsRaw = params.get("c");
  if (columnsRaw) {
    hasCompactKey = true;
    const decodedColumns = decodeVisibleColumnsBitset(columnsRaw);
    if (decodedColumns.length > 0) {
      visibleColumns = decodedColumns;
    }
  }

  if (!hasCompactKey) return null;
  return { filters, sortColumns, visibleColumns };
}
